import { useState, useEffect } from "react";
import Calendar from "react-calendar"; // ⬅️ NUEVO
import "react-calendar/dist/Calendar.css"; // ⬅️ NUEVO

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FilePenLine, DoorOpen, Filter, PlusCircle, CalendarDays, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { sanitizeUrl } from "@braintree/sanitize-url";
import { ficharService } from "@/services/ficharService";
import { getSession } from "@/lib/session";
import CorreccionesFichajes from "@/components/home/CorreccionesFichajes";

import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";

const baseUrl = import.meta.env.VITE_BASE_URL;

const formatDate = (iso) => {
  if (!iso) return "";
  const parts = iso.split("-");
  if (parts.length < 3) return iso;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  return d.toLocaleDateString("es-ES");
};

const typeLabels = {
  accidente_trabajo: "Accidente de trabajo",
  cita_medica: "Cita médica",
  enfermedad: "Enfermedad",
  matrimonio: "Matrimonio",
  nacimiento_hijo: "Nacimiento de un hijo",
  ausencia: "Ausencia",
  traslado_domicilio: "Traslado de domicilio",
  descanso: "Descanso",
  vacaciones: "Vacaciones",
  otro: "Otro",
};

const Solicitudes = () => {
  const [showDesde, setShowDesde] = useState(false);
  const [showHasta, setShowHasta] = useState(false);

  const [dateDesde, setDateDesde] = useState<Date | null>(null);
  const [dateHasta, setDateHasta] = useState<Date | null>(null);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);

  const [cancelId, setCancelId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [activeSection, setActiveSection] = useState("fichajes");
  const [loading, setLoading] = useState(true);
  const [userHash, setUserHash] = useState(null);

  const [showIframeModal, setShowIframeModal] = useState(false);
  const [requestType, setRequestType] = useState("");

  const [filters, setFilters] = useState({
    estado: "",
    categoria: "",
    desde: "",
    hasta: "",
  });

  const [list, setList] = useState([]);

  // NUEVO: Vista Listado o Calendario
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");

  // Carga usuario
  useEffect(() => {
    const loadUser = async () => {
      const parsed = await getSession();
      if (!parsed) return;

      const id = parsed?.id;
      if (!id) return;

      setLoading(true);
      try {
        const r = await ficharService.getDataEmployee(id);
        setUserHash(r.response.hash);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  // Carga listado
  useEffect(() => {
    if (activeSection !== "vacaciones") return;
    if (!userHash) return;

    const loadList = async () => {
      try {
        const res = await fetch(baseUrl + "ws/solicitudesausencia/" + userHash);
        const data = await res.json();

        if (!Array.isArray(data.response)) {
          setList([]);
          return;
        }

        let mapped = data.response.map((i) => ({
          id: i.id,
          estado: i.status?.toLowerCase() ?? "",
          categoria: i.type ?? "",
          fecha_inicio: i.date?.split(" ")[0] ?? "",
          fecha_fin: i.date?.split(" ")[0] ?? "",
          raw: i,
        }));

        // Aplicar filtros
        if (filters.estado)
          mapped = mapped.filter(
            (i) => i.estado === filters.estado || filters.estado === "todos"
          );

        if (filters.categoria)
          mapped = mapped.filter(
            (i) => i.categoria === filters.categoria || filters.categoria === "todos"
          );

        if (filters.desde)
          mapped = mapped.filter((i) => i.fecha_inicio >= filters.desde);

        if (filters.hasta)
          mapped = mapped.filter((i) => i.fecha_fin <= filters.hasta);

        // Formateo de fechas
        mapped = mapped.map((i) => ({
          ...i,
          fecha_inicio: formatDate(i.fecha_inicio),
          fecha_fin: formatDate(i.fecha_fin),
        }));

        setList(mapped);
      } catch (e) {
        console.error("Error cargando datos:", e);
        setList([]);
      }
    };

    loadList();
  }, [activeSection, filters, userHash]);

  // Cancelar solicitud
  const cancelarSolicitud = async () => {
    if (!cancelId || !userHash) return;

    setIsCancelling(true);

    try {
      await fetch(baseUrl + "ws/anularsolicitudesausencia/" + userHash + "/" + cancelId);
      setConfirmCancel(false);
      setCancelId(null);
      setFilters({ ...filters });
    } catch (e) {
      console.error(e);
    }

    setIsCancelling(false);
  };

  // LOADING
  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-900">
        <img src={logo}     alt="Logo" className="h-20 w-auto mb-4 dark:hidden" />
        <img src={logoDark} alt="Logo" className="h-20 w-auto mb-4 hidden dark:block" />
        <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500">Cargando...</p>
      </div>
    );
  }

  // 🎯  Obtener colores por estado
  const getColor = (estado) => {
    return estado === "aprobada"
      ? "green"
      : estado === "rechazado"
      ? "red"
      : estado === "anulada"
      ? "black"
      : "orange";
  };

  // 🎯  Eventos para el calendario
  const eventsByDate = {};
  list.forEach((x) => {
    const fecha = x.fecha_inicio;
    if (!eventsByDate[fecha]) eventsByDate[fecha] = [];
    eventsByDate[fecha].push(x);
  });

  return (
    <div className="space-y-4 animate-fade-in">

      {/* NAV */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant={activeSection === "fichajes" ? "default" : "outline"}
          className="h-12 text-sm flex items-center justify-center gap-2"
          onClick={() => setActiveSection("fichajes")}
        >
          <FilePenLine className="w-4 h-4" /> Corrección
        </Button>

        <Button
          variant={activeSection === "vacaciones" ? "default" : "outline"}
          className="h-12 text-sm flex items-center justify-center gap-2"
          onClick={() => setActiveSection("vacaciones")}
        >
          <DoorOpen className="w-4 h-4" /> Permisos
        </Button>
      </div>

      {/* FICHAJES */}
      {activeSection === "fichajes" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Corrección de fichajes</CardTitle>
          </CardHeader>
          <CardContent>
            <CorreccionesFichajes userHash={userHash} />
          </CardContent>
        </Card>
      )}

      {/* VACACIONES */}
      {activeSection === "vacaciones" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-xl">Vacaciones y permisos</CardTitle>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="flex items-center gap-2 h-9 text-sm px-3">
                  <PlusCircle className="w-4 h-4" /> Nueva
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => { setRequestType("bajamedica"); setShowIframeModal(true); }}>Baja médica</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setRequestType("vacaciones"); setShowIframeModal(true); }}>Vacaciones</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setRequestType("permisos"); setShowIframeModal(true); }}>Permisos</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>

          <CardContent className="space-y-6">

            {/* VISTA: LISTADO / CALENDARIO */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                className="flex items-center justify-center gap-2 h-10 text-sm"
                onClick={() => setViewMode("list")}
              >
                <List className="w-4 h-4" /> Listado
              </Button>

              <Button
                variant={viewMode === "calendar" ? "default" : "outline"}
                className="flex items-center justify-center gap-2 h-10 text-sm"
                onClick={() => setViewMode("calendar")}
              >
                <CalendarDays className="w-4 h-4" /> Calendario
              </Button>
            </div>

            {/* FILTROS */}
            {viewMode === "list" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-muted p-4 rounded-xl">

                  {/* ESTADO */}
                  <Select onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="aprobada">Aprobada</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="rechazado">Rechazado</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* CATEGORÍA */}
                  <Select onValueChange={(v) => setFilters({ ...filters, categoria: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {Object.entries(typeLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* DESDE */}
                  <Input
                    readOnly
                    placeholder="Desde"
                    value={dateDesde ? dateDesde.toLocaleDateString("es-AR") : ""}
                    onClick={() => setShowDesde(true)}
                    className="cursor-pointer bg-background"
                  />

                  {/* HASTA */}
                  <Input
                    readOnly
                    placeholder="Hasta"
                    value={dateHasta ? dateHasta.toLocaleDateString("es-AR") : ""}
                    onClick={() => setShowHasta(true)}
                    className="cursor-pointer bg-background"
                  />
                </div>

                {/* MODAL DESDE */}
                <Dialog open={showDesde} onOpenChange={setShowDesde}>
                  <DialogContent className="max-w-sm p-4">
                    <DialogHeader>
                      <DialogTitle>Fecha desde</DialogTitle>
                    </DialogHeader>

                    <Calendar
                      locale="es-AR"
                      onChange={(d) => {
                        const date = d as Date;
                        setDateDesde(date);
                        setFilters({
                          ...filters,
                          desde: date.toISOString().split("T")[0],
                        });
                        setShowDesde(false);
                      }}
                    />
                  </DialogContent>
                </Dialog>

                {/* MODAL HASTA */}
                <Dialog open={showHasta} onOpenChange={setShowHasta}>
                  <DialogContent className="max-w-sm p-4">
                    <DialogHeader>
                      <DialogTitle>Fecha hasta</DialogTitle>
                    </DialogHeader>

                    <Calendar
                      locale="es-AR"
                      onChange={(d) => {
                        const date = d as Date;
                        setDateHasta(date);
                        setFilters({
                          ...filters,
                          hasta: date.toISOString().split("T")[0],
                        });
                        setShowHasta(false);
                      }}
                    />
                  </DialogContent>
                </Dialog>
              </>
            )}


            {/* 📌 VISTA LISTADO */}
            {viewMode === "list" && (
              <>
                {list.length === 0 && (
                  <p className="text-center text-muted-foreground py-6">
                    <Filter className="inline w-5 h-5 mr-2" />
                    No hay solicitudes para mostrar
                  </p>
                )}

                <div className="grid gap-4">
                  {list.map((item) => (
                    <Card
                      key={item.id}
                      className="border-l-4"
                      style={{ borderColor: getColor(item.estado) }}
                    >
                      <CardContent className="p-5">
                        {item.estado === "pendiente" && (
                          <Button
                            className="relative mt-[10px] h-[20px] w-[20px] p-0 float-right"
                            variant="destructive"
                            onClick={() => {
                              setCancelId(item.id);
                              setConfirmCancel(true);
                            }}
                          >
                            X
                          </Button>
                        )}
                        <p className={`w-full text-center px-2 py-1 text-sm font-semibold rounded-md
                            ${
                              item.estado === "aprobada"
                                ? "bg-green-100 text-green-700"
                                : item.estado === "rechazado"
                                ? "bg-red-100 text-red-700"
                                : item.estado === "anulada"
                                ? "bg-gray-900 text-white"
                                : "bg-yellow-100 text-yellow-700"
                            }
                          `}
                        >
                          {item.estado}
                        </p>
                        <div className="flex justify-between">
                          <div>
                            <h3 className="font-semibold text-md">
                              {typeLabels[item.categoria]}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {item.fecha_inicio} → {item.fecha_fin}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}

            {/* VISTA CALENDARIO */}
            {viewMode === "calendar" && (
              <div className="space-y-6">

                <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-xl">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-green-600 rounded"></span> Aprobada
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-yellow-500 rounded"></span> Pendiente
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-red-600 rounded"></span> Rechazada
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 bg-black rounded"></span> Anulada
                  </div>
                </div>

                <div className="w-full border rounded-xl overflow-hidden shadow-sm">
                  <Calendar
                    locale="es-AR"
                    className="w-full"
                    tileClassName="p-1"
                    tileContent={({ date }) => {
                      const key = date.toLocaleDateString("es-AR");
                      const evs = eventsByDate[key];
                      if (!evs) return null;

                      return (
                        <div className="mt-1 flex flex-col gap-1">
                          {evs.map((ev) => (
                            <button
                              key={ev.id}
                              onClick={() => {
                                setSelectedEvent(ev);
                                setShowEventModal(true);
                              }}
                              className="text-[10px] text-white px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition"
                              style={{ background: getColor(ev.estado) }}
                            >
                              {typeLabels[ev.categoria].slice(0, 10)}…
                            </button>
                          ))}
                        </div>
                      );
                    }}
                  />
                </div>

                {/* ⭐ Modal detalle del evento */}
                <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Detalle de ausencia</DialogTitle>
                    </DialogHeader>

                    {selectedEvent && (
                      <div className="space-y-3">
                        <p><b>Tipo:</b> {typeLabels[selectedEvent.categoria]}</p>
                        <p><b>Estado:</b> {selectedEvent.estado}</p>
                        <p><b>Fecha:</b> {selectedEvent.fecha_inicio}</p>
                      </div>
                    )}

                    <div className="flex justify-end mt-6">
                      {selectedEvent && selectedEvent.estado === "pendiente" && (
                        <Button
                            className="mr-10"
                            variant="destructive"
                            onClick={() => {
                              setShowEventModal(false);
                              setCancelId(selectedEvent.id);
                              setConfirmCancel(true);
                            }}
                          >
                            Anular
                          </Button>
                      )}
                      <Button variant="outline" onClick={() => setShowEventModal(false)}>
                        Cerrar
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

              </div>
            )}


          </CardContent>
        </Card>
      )}

      {/* ---------- MODALES ---------- */}

      <Dialog
        open={showIframeModal}
        onOpenChange={(o) => {
          setShowIframeModal(o);
          if (!o) setFilters({ ...filters });
        }}
      >
        <DialogContent className="w-[95vw] max-w-3xl p-3">
          <DialogHeader><DialogTitle>Nueva solicitud</DialogTitle></DialogHeader>

          <div className="w-full h-[70vh] border rounded-xl overflow-hidden">
            <iframe
              src={sanitizeUrl(`${baseUrl}solicitudausencia/${requestType}/code/${userHash}`)}
              className="w-full h-full"
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>¿Anular solicitud?</DialogTitle>
          </DialogHeader>

          <p className="text-muted-foreground mt-2">
            ¿Estás seguro de que deseas anular esta solicitud?
          </p>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmCancel(false)}>
              No
            </Button>

            <Button variant="destructive" onClick={cancelarSolicitud} disabled={isCancelling}>
              {isCancelling ? "Anulando..." : "Sí, anular"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Solicitudes;
